import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MaintenanceRecordCard } from './MaintenanceRecordCard';
import { usePondNames } from '@/features/shared/hooks/usePondNames';
import { maintenanceApi } from '@/api/maintenanceApi';
import type { MaintenanceCheck } from '@/api/maintenanceApi';
import { useErrorToast } from '@/shared/hooks/useErrorToast';
import { SectionLoading } from '@/shared/components/ui/LoadingSpinner';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { X, Filter, Wrench } from 'lucide-react';

type MaintenanceFilter = 'equipment_issues' | 'algal_bloom' | 'birds' | 'stressed_fish';

const filterLabels: Record<MaintenanceFilter, string> = {
  equipment_issues: 'Equipment Issues',
  algal_bloom: 'Algal Blooms',
  birds: 'Bird Presence',
  stressed_fish: 'Stressed Fish',
};

interface MaintenanceRecordsTabProps {
  farmId: string;
  selectedPondId: string | null;
}

export const MaintenanceRecordsTab: React.FC<MaintenanceRecordsTabProps> = ({
  farmId,
  selectedPondId,
}) => {
  const { showAxiosError } = useErrorToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const pondNames = usePondNames(farmId);
  const [checks, setChecks] = useState<MaintenanceCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  // Read filter params from URL
  const filter = searchParams.get('filter') as MaintenanceFilter | null;
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const clearFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('filter');
    newParams.delete('startDate');
    newParams.delete('endDate');
    setSearchParams(newParams, { replace: true });
  };

  const loadChecks = useCallback(async (cursor?: string) => {
    try {
      setLoading(true);
      const response = await maintenanceApi.listChecks(farmId, {
        cursor,
        limit: 20,
        pondId: selectedPondId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        filter: filter || undefined,
      });

      if (cursor) {
        setChecks((prev) => [...prev, ...(response.data || [])]);
      } else {
        setChecks(response.data || []);
      }

      setNextCursor(response.pagination?.nextCursor);
      setHasMore(response.pagination?.hasNext || false);
    } catch (error) {
      showAxiosError(error);
      // Ensure checks is always an array even on error
      if (!cursor) {
        setChecks([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [farmId, selectedPondId, startDate, endDate, filter, showAxiosError]);

  useEffect(() => {
    loadChecks();
  }, [loadChecks]);

  return (
    <div className="space-y-4">
      {/* Filter Indicator */}
      {filter && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200/50 rounded-lg">
          <Filter className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-blue-800">
            Showing records with: <strong>{filterLabels[filter]}</strong>
            {startDate && endDate && (
              <span className="text-blue-600 ml-1">
                ({new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()})
              </span>
            )}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilter}
            className="ml-auto h-6 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
      )}

      {/* Records List */}
      {loading && checks.length === 0 ? (
        <SectionLoading message="Loading maintenance checks..." />
      ) : checks.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 border border-gray-200/50 mb-4">
              <Wrench className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="font-roboto text-sm font-semibold text-gray-900">
              No maintenance checks recorded
            </h3>
            <p className="font-roboto mt-1 text-sm text-gray-600">
              {filter
                ? `No records found with ${filterLabels[filter]}`
                : selectedPondId
                ? 'No maintenance checks found for the selected pond.'
                : 'Maintenance checks from the mobile app will appear here.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {checks.map((check) => (
            <MaintenanceRecordCard key={check.id} check={check} farmId={farmId} pondName={pondNames[check.pondId]} />
          ))}
        </div>
      )}

      {/* Load More */}
      {hasMore && nextCursor && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            onClick={() => loadChecks(nextCursor)}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
};
